<template>
    <div>
        <div class="kgr-grid kgr-grid--full kgr-grid--medium-1of6 kgr-grid--gutters kgr-row">
            <div class="kgr-grid__cell">
                <div class="kgr-inputGroup">
                    <div
                        class="kgr-inputGroup__label"
                    >
                        {{ $t('Prüfungsdatum ab') }}
                    </div>
                    <kgr-date-picker
                        v-model="filter.startDate"
                        :invalid="!isValidDateRange || !isValidStartDate"
                    />
                </div>
            </div>
            <div class="kgr-grid__cell">
                <div class="kgr-inputGroup">
                    <div
                        class="kgr-inputGroup__label"
                    >
                        {{ $t('Prüfungsdatum bis') }}
                    </div>
                    <kgr-date-picker
                        v-model="filter.endDate"
                        :invalid="!isValidDateRange || !isValidEndDate"
                    />
                </div>
            </div>
        </div>
        <div class="kgr-grid kgr-grid--full kgr-grid--medium-1of6 kgr-grid--gutters kgr-row">
            <div class="kgr-grid__cell">
                <label class="kgr-inputGroup">
                    <span class="kgr-inputGroup__label">{{ $t('club.exam.title') }}</span>
                    <kgr-multi-select
                        v-model="filter.exams"
                        :options="filteredExams"
                        :loading="loading"
                        display="title"
                        searchable
                    />
                </label>
            </div>
            <div class="kgr-grid__cell">
                <label class="kgr-inputGroup">
                    <span class="kgr-inputGroup__label">{{ $t('club.exam.type') }}</span>
                    <kgr-multi-select
                        v-model="filter.examTypes"
                        :options="filteredExamTypes"
                        :loading="loading"
                        display="title"
                        searchable
                    />
                </label>
            </div>
            <div class="kgr-grid__cell">
                <label class="kgr-inputGroup">
                    <span class="kgr-inputGroup__label">{{ $t('club.exam.sport') }}</span>
                    <kgr-multi-select
                        v-model="filter.examSports"
                        :options="filteredSports"
                        :loading="loading"
                        display="name"
                        searchable
                    />
                </label>
            </div>
            <div class="kgr-grid__cell">
                <label class="kgr-inputGroup">
                    <span class="kgr-inputGroup__label">{{ $t('club.exam.result.status') }}</span>
                    <kgr-generic-select
                        v-model="filter.resultStatus"
                        :options="availableResultStatus"
                        display="name"
                    />
                </label>
            </div>
            <div class="kgr-grid__cell">
                <div class="kgr-inputGroup__label">
&nbsp;
                </div>
                <button
                    class="kgr-button kgr-button--accent"
                    :disabled="buttonDisabled"
                    @click="loadResults"
                >
                    {{ $t('Filtern') }}
                </button>
            </div>
            <div
                v-if="showExportButton"
                class="kgr-grid__cell"
            >
                <div class="kgr-inputGroup__label">
                    &nbsp;
                </div>
                <button
                    class="kgr-button kgr-button--accent"
                    :disabled="buttonDisabled"
                    @click="exportResults"
                >
                    {{ $t('Excel-Export') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { isMatch, parse } from '@legacy/js/date/date-fns.js';
import ClubExamRepository from '../../js/repository/club/ClubExamRepository.js';
import ClubExamResult from '../../js/dto/club/ClubExamResult';
import ClubExamTypeRepository from '../../js/repository/club/ClubExamTypeRepository.js';
import DatePicker from '../common/form/picker/DatePicker.vue';
import GenericSelect from '../common/form/select/GenericSelect.vue';
import MultiSelect from '../common/form/select/MultiSelect.vue';
import SportRepository from '../../js/repository/SportsRepository.js';

export default {
    components: {
        'kgr-multi-select': MultiSelect,
        'kgr-generic-select': GenericSelect,
        'kgr-date-picker': DatePicker,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        loading: {
            type: Boolean,
            required: true,
        },
        storageKey: {
            type: String,
            required: true,
        },
        clubExamId: {
            type: String,
            required: false,
            default: null,
        },
        showExportButton: {
            type: Boolean,
            required: false,
            default: false,
        },
        examineeType: {
            type: String,
            required: false,
            default: null,
        },
    },
    data() {
        return {
            availableExams: [],
            availableTypes: [],
            availableSports: [],
            availableResultStatus: [
                { name: this.$t('club.exam.result.status.success'), value: ClubExamResult.STATUS_SUCCESS },
                { name: this.$t('club.exam.result.status.failure'), value: ClubExamResult.STATUS_FAILURE },
                { name: this.$t('club.exam.result.status.pending'), value: ClubExamResult.STATUS_PENDING },
            ],
            filter: {
                startDate: null,
                endDate: null,
                exams: [],
                examTypes: [],
                examSports: [],
                resultStatus: null,
            },
        };
    },
    computed: {
        filteredSports() {
            return this.availableSports.filter((el) => this.filter.examSports.indexOf(el) === -1);
        },
        filteredExams() {
            return this.availableExams.filter((el) => this.filter.exams.indexOf(el) === -1);
        },
        filteredExamTypes() {
            return this.availableTypes.filter((el) => this.filter.examTypes.indexOf(el) === -1);
        },
        filterParams() {
            return {
                examIds: this.filter.exams.map((e) => e.id),
                examTypeIds: this.filter.examTypes.map((e) => e.id),
                sportIds: this.filter.examSports.map((e) => e.id),
                resultStatus: this.filter.resultStatus ? this.filter.resultStatus.value : null,
                startDate: this.filter.startDate,
                endDate: this.filter.endDate,
            };
        },
        isValidDateRange() {
            if (!this.filter.endDate || !this.filter.startDate) {
                return true;
            }

            return parse(this.filter.startDate, 'dd.MM.yyyy', new Date()) <= parse(this.filter.endDate, 'dd.MM.yyyy', new Date());
        },
        isValidStartDate() {
            // regex check necessary until https://github.com/date-fns/date-fns/issues/3851 is fixed
            if (this.filter.startDate && !/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.test(this.filter.startDate)) {
                return false;
            }
            return !this.filter.startDate || isMatch(this.filter.startDate, 'dd.MM.yyyy');
        },
        isValidEndDate() {
            if (this.filter.endDate && !/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.test(this.filter.endDate)) {
                return false;
            }
            return !this.filter.endDate || isMatch(this.filter.endDate, 'dd.MM.yyyy');
        },
        isInputValid() {
            return this.isValidDateRange && this.isValidStartDate && this.isValidEndDate;
        },
        buttonDisabled() {
            return this.loading || !this.isInputValid;
        },
    },
    created() {
        this.loadExams();
        this.loadExamTypes();
        this.loadSports();
        if (!this.clubExamId) {
            const examResultFilter = localStorage.getItem(this.storageKey);
            if (examResultFilter) {
                this.filter = JSON.parse(examResultFilter);
                this.loadResults();
            }
        }
    },
    methods: {
        loadResults() {
            this.persistLocalStorage();
            this.$emit('onFilter', this.filterParams);
        },
        exportResults() {
            this.persistLocalStorage();
            this.$emit('onExport', this.filterParams);
        },
        loadExams() {
            const params = {};
            if (this.examineeType) {
                params.examineeType = this.examineeType;
            }
            ClubExamRepository.findByClubId(this.clubId, params)
                .then((data) => {
                    this.availableExams = data;
                    if (this.clubExamId) {
                        this.setFilterAndEmitOnFilter();
                    }
                })
                .catch(() => this.$notify.error('error.loadingData'));
        },
        loadExamTypes() {
            ClubExamTypeRepository
                .findByClubId(this.clubId, false)
                .then((data) => {
                    this.availableTypes = data;
                })
                .catch(() => this.$notify.error('error.loadingData'));
        },
        loadSports() {
            SportRepository
                .findAll(this.clubId)
                .then((data) => {
                    this.availableSports = data;
                })
                .catch(() => this.$notify.error('error.loadingData'));
        },
        persistLocalStorage() {
            localStorage.setItem(this.storageKey, JSON.stringify(this.filter));
        },
        setFilterAndEmitOnFilter() {
            const exams = this.availableExams.filter((element) => element.id === this.clubExamId);
            if (exams.length === 1) {
                this.filter.exams.push(exams[0]);
                this.$emit('onFilter', this.filterParams);
            }
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss">
</style>
